import React, { useState } from 'react';

export const GoogleAdsDiagnostics: React.FC = () => {
  const [diagnostics, setDiagnostics] = useState<any>(null);

  const runDiagnostics = () => {
    if (typeof window === 'undefined') return;

    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : '';
    const origin = `${protocol}//${hostname}${port}`;
    const currentUrl = window.location.href;

    const detectedRedirectUri = (() => {
      if (hostname === 'operationsfaircareplus.web.app' || hostname.endsWith('.web.app')) {
        return 'https://operationsfaircareplus.web.app/auth/google-ads/callback';
      }
      if (hostname === 'operations.faircareplus.com' || hostname.includes('faircareplus.com')) {
        return 'https://operations.faircareplus.com/auth/google-ads/callback';
      }
      return `${origin}/auth/google-ads/callback`;
    })();

    const info = {
      timestamp: new Date().toISOString(),
      environment: {
        hostname,
        protocol,
        port,
        origin,
        currentUrl,
        userAgent: navigator.userAgent,
      },
      redirectUri: {
        detected: detectedRedirectUri,
        expectedUris: [
          'https://operations.faircareplus.com/auth/google-ads/callback',
          'https://operationsfaircareplus.web.app/auth/google-ads/callback',
          'http://localhost:3001/auth/google-ads/callback',
        ],
      },
      oauth: {
        clientId: '731027622622-0rpsloo1v3apc41iuvfk1a1qjddb3c37.apps.googleusercontent.com',
        scope: 'https://www.googleapis.com/auth/adwords',
      },
      recommendations: [] as string[],
    };

    // Check for mismatches
    if (detectedRedirectUri === 'https://operations.faircareplus.com/auth/google-ads/callback') {
      if (hostname !== 'operations.faircareplus.com') {
        info.recommendations.push(
          `⚠️ Hostname mismatch: Using custom domain URI but hostname is "${hostname}"`
        );
      }
    }

    if (detectedRedirectUri === 'https://operationsfaircareplus.web.app/auth/google-ads/callback') {
      if (!hostname.includes('.web.app')) {
        info.recommendations.push(
          `⚠️ Hostname mismatch: Using Firebase hosting URI but hostname is "${hostname}"`
        );
      }
    }

    // Check if detected URI is in expected list
    if (!info.redirectUri.expectedUris.includes(detectedRedirectUri)) {
      info.recommendations.push(
        `❌ Detected redirect URI "${detectedRedirectUri}" is NOT in the expected list!`
      );
    }

    // Check for typos
    if (hostname.includes('farcareplus') && !hostname.includes('faircareplus')) {
      info.recommendations.push(
        `❌ TYPO DETECTED: Hostname contains "farcareplus" instead of "faircareplus"`
      );
    }

    setDiagnostics(info);
    console.log('Google Ads OAuth Diagnostics:', info);
  };

  const testOAuthUrl = () => {
    if (!diagnostics) {
      runDiagnostics();
      setTimeout(testOAuthUrl, 100);
      return;
    }

    const redirectUri = diagnostics.redirectUri.detected;
    const params = new URLSearchParams({
      client_id: diagnostics.oauth.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: diagnostics.oauth.scope,
      access_type: 'offline',
      prompt: 'consent',
    });

    const authUrl = `https://accounts.google.com/o/oauth2/auth?${params.toString()}`;
    console.log('Generated OAuth URL:', authUrl);
    console.log('Redirect URI in URL:', redirectUri);

    // Open in new window for testing
    const testWindow = window.open(authUrl, '_blank', 'width=800,height=600');
    if (testWindow) {
      setTimeout(() => {
        testWindow.close();
      }, 5000);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">OAuth Diagnostics</h3>
          <p className="text-sm text-gray-500">
            Diagnostic tool to check OAuth configuration and detect issues
          </p>
        </div>
        <button
          onClick={runDiagnostics}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
        >
          Run Diagnostics
        </button>
      </div>

      {diagnostics && (
        <div className="space-y-4">
          {/* Environment Info */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Environment Information</h4>
            <div className="space-y-1 text-sm font-mono">
              <div>
                <span className="text-gray-600">Hostname:</span>{' '}
                <span className="text-gray-900">{diagnostics.environment.hostname}</span>
              </div>
              <div>
                <span className="text-gray-600">Protocol:</span>{' '}
                <span className="text-gray-900">{diagnostics.environment.protocol}</span>
              </div>
              <div>
                <span className="text-gray-600">Origin:</span>{' '}
                <span className="text-gray-900">{diagnostics.environment.origin}</span>
              </div>
              <div>
                <span className="text-gray-600">Current URL:</span>{' '}
                <span className="text-gray-900 break-all">{diagnostics.environment.currentUrl}</span>
              </div>
            </div>
          </div>

          {/* Redirect URI Info */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Redirect URI Configuration</h4>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-600">Detected Redirect URI:</span>
                <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded font-mono text-sm break-all">
                  {diagnostics.redirectUri.detected}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Expected URIs (in Google Cloud Console):</span>
                <ul className="mt-1 space-y-1">
                  {diagnostics.redirectUri.expectedUris.map((uri: string, idx: number) => (
                    <li
                      key={idx}
                      className={`p-2 rounded font-mono text-sm break-all ${
                        uri === diagnostics.redirectUri.detected
                          ? 'bg-green-50 border border-green-200'
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      {uri === diagnostics.redirectUri.detected && '✓ '}
                      {uri}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          {diagnostics.recommendations.length > 0 && (
            <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 mb-2">⚠️ Recommendations</h4>
              <ul className="space-y-1 text-sm text-yellow-800">
                {diagnostics.recommendations.map((rec: string, idx: number) => (
                  <li key={idx}>{rec}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Test Button */}
          <div className="flex justify-end">
            <button
              onClick={testOAuthUrl}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm font-medium"
            >
              Test OAuth URL (Opens in New Window)
            </button>
          </div>

          {/* Copy to Clipboard */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
                alert('Diagnostics copied to clipboard!');
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Copy Diagnostics to Clipboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

